import type {
  LiveUpdateState,
  LiveUpdateTransport,
} from './live-update-transport';

/**
 * Connection health: whether this page is offline, signed out, or merely waiting for live updates,
 * decided from the server's own answers and never from the live-update connection alone (D-137,
 * documentation/ui-qa/notifications/connection-health-acceptance.md).
 *
 * Three signals, one owner each (rule 1):
 * - REACHABLE: the server answers its anonymous liveness check, `/healthz`.
 * - SIGNED IN: the server answers `GET /api/SessionHealth` naming the person this tab acts as.
 * - LIVE UPDATES: the `LiveUpdateTransport` says connected, reconnecting or disconnected.
 *
 * The decision is a pure function of those, so the state table is proved by unit tests without a
 * browser; the monitor below only decides WHEN to ask and hands the answers to it.
 */

/**
 * - `online`: nothing to show.
 * - `reconnecting`: live updates are down but the server is reachable and the sign-in holds, for
 *   under a minute. Quiet: the header's activity indicator shows it (rule 2).
 * - `live-updates-failing`: the same for a minute or more. A persistent on-screen notice says a
 *   developer may need to investigate; the page is not blocked (rule 6).
 * - `offline`: the server cannot be reached, or the browser says it has no network. The blocking
 *   overlay (rule 3).
 * - `signed-out`: the sign-in check was refused or named someone else. The existing sign-out path
 *   and the sign-in page, never the overlay (rule 4).
 */
export type ConnectionHealthState =
  'online' | 'reconnecting' | 'live-updates-failing' | 'offline' | 'signed-out';

/**
 * What the last sign-in check said.
 * - `ok`: it answered for the person this tab acts as.
 * - `refused`: 401 or 403, the sign-in has ended.
 * - `mismatch`: it answered for someone else, so this tab is no longer acting as who it thinks.
 * - `unknown`: no usable answer (a server error, a timeout). Not treated as signed out: only a
 *   refusal or a mismatch proves that.
 * - `not-signed-in`: this tab has nobody signed in, so there is nothing to check.
 */
export type SignInResult =
  'ok' | 'refused' | 'mismatch' | 'unknown' | 'not-signed-in';

/**
 * Why the monitor decided `signed-out`, handed to the sign-out so it can act on the difference
 * (D-143). A refusal means the server has already ended this tab's sign-in, so there is nothing
 * left there to end. A mismatch means the server still holds this tab's session and its locks,
 * so the sign-out must end them on the server before forgetting them locally.
 */
export type SignedOutReason = Extract<SignInResult, 'refused' | 'mismatch'>;

export interface ConnectionHealthInputs {
  /** The live-update connection's own state. */
  transport: LiveUpdateState;
  /** The last reachability answer; undefined until one has been asked, which is not evidence of offline. */
  reachable: boolean | undefined;
  /** The last sign-in answer. */
  signIn: SignInResult;
  /** When the live-update connection last stopped being connected; undefined while connected. */
  transportDownSince: number | undefined;
  /** Now, in the same clock as `transportDownSince`. */
  now: number;
  /** `navigator.onLine`: false is the browser saying it has no network at all. */
  navigatorOnline: boolean;
}

/** How long live updates may be down, with everything else fine, before the developer notice. */
export const LIVE_UPDATES_FAILING_AFTER_MS = 60_000;

/**
 * The state table (rules 2, 3, 4, 6), in priority order:
 * 1. No network, or the server did not answer: offline. Nothing else can be known.
 * 2. The sign-in was refused or names someone else: signed out.
 * 3. Live updates connected: online.
 * 4. Live updates down a minute or more, with the server answering and the sign-in holding (or
 *    nobody signed in to check): live updates failing.
 * 5. Otherwise: reconnecting, which covers "down under a minute" and "down but a check could not
 *    be answered yet".
 */
export function decideConnectionHealth(
  inputs: ConnectionHealthInputs
): ConnectionHealthState {
  if (!inputs.navigatorOnline || inputs.reachable === false) return 'offline';
  if (inputs.signIn === 'refused' || inputs.signIn === 'mismatch') {
    return 'signed-out';
  }
  if (inputs.transport === 'connected') return 'online';

  const downFor =
    inputs.transportDownSince === undefined
      ? 0
      : inputs.now - inputs.transportDownSince;
  const everyCheckSucceeds =
    inputs.reachable === true &&
    (inputs.signIn === 'ok' || inputs.signIn === 'not-signed-in');
  if (downFor >= LIVE_UPDATES_FAILING_AFTER_MS && everyCheckSucceeds) {
    return 'live-updates-failing';
  }
  return 'reconnecting';
}

/**
 * The waits between reachability checks while offline: 2, 4, 8 and 16 seconds, then every 30
 * seconds for as long as it takes. Short at first because most outages are a restart or a blip;
 * capped so a long outage costs the server one request per open tab every half minute.
 */
export const OFFLINE_BACKOFF_MS = [2_000, 4_000, 8_000, 16_000] as const;
export const OFFLINE_STEADY_MS = 30_000;

/** How often the sign-in is re-checked while the tab is in view (rule 5). */
export const SIGN_IN_CHECK_INTERVAL_MS = 5 * 60_000;

/** The wait before the n-th reachability check of an outage (0-based). */
export function offlineBackoffMs(attempt: number): number {
  return attempt < OFFLINE_BACKOFF_MS.length
    ? OFFLINE_BACKOFF_MS[attempt]
    : OFFLINE_STEADY_MS;
}

/** What the monitor needs from the outside world; injected so its timing is unit-testable. */
export interface ConnectionHealthDeps {
  transport: LiveUpdateTransport;
  /** Asks `/healthz`; true when the server answered. Never throws. */
  checkReachable(): Promise<boolean>;
  /** Asks `/api/SessionHealth` and compares the answer with this tab's user. Never throws. */
  checkSignIn(): Promise<SignInResult>;
  /** Told every change of state, after the change. */
  onState(state: ConnectionHealthState): void;
  /**
   * Told on entering `signed-out`, with which answer caused it: runs the sign-out path (rule 4),
   * which for a mismatch ends this tab's session on the server first (D-143).
   */
  onSignedOut(reason: SignedOutReason): void;
  /**
   * Told when a request went unanswered but the check it set off found the server answering and
   * the sign-in holding: a single failure, shown as one short notice (rule 8, D-144). Never told
   * for an outage, which the offline overlay shows instead.
   */
  onRequestUnanswered(): void;
}

/**
 * How long after an unanswered request further ones count as the same burst, and get neither a
 * check nor a notice of their own (rule 8: several failures in one burst give at most one of each).
 * Ten seconds because one action and the loads the page runs beside it fail together, within the
 * client's own retries (a few seconds at most); a failure a person meets later, after acting again,
 * is a new event and is told again. Leaving offline starts a new window at once.
 */
export const UNANSWERED_BURST_WINDOW_MS = 10_000;

/**
 * Decides WHEN to ask the server, and hands every answer to `decideConnectionHealth` (rule 5):
 * - live updates leave connected, or are already down when the monitor starts: reachability, then
 *   the sign-in; and again every minute while they stay down (the first of these is when the
 *   developer notice may apply);
 * - a sign-in check with no usable answer: reachability too, since the server may be gone;
 * - live updates connect or reconnect: the sign-in (a connection can open without proving who the
 *   caller is: the stream accepts anonymous callers);
 * - the tab comes into view, and every 5 minutes while it is in view: the sign-in;
 * - the browser reports it is offline: offline at once; online again: both checks at once;
 * - a request goes unanswered (rule 8): both checks at once, then one notice if the server answered;
 * - while offline: reachability on the backoff above, then the sign-in, then the table, and when
 *   the page has left offline, `transport.reconnectNow()` so live updates return with the server.
 *
 * Checks run one at a time, in the order asked, so two answers never interleave into a state
 * neither of them describes.
 */
export class ConnectionHealthMonitor {
  private _state: ConnectionHealthState = 'online';
  private reachable: boolean | undefined;
  private signIn: SignInResult = 'not-signed-in';
  private transportState: LiveUpdateState;
  private transportDownSince: number | undefined;

  private offlineAttempt = 0;
  private offlineTimer: ReturnType<typeof setTimeout> | undefined;
  private noticeTimer: ReturnType<typeof setTimeout> | undefined;
  private signInInterval: ReturnType<typeof setInterval> | undefined;
  private queue: Promise<void> = Promise.resolve();
  private disposers: (() => void)[] = [];
  private started = false;
  /** Until when further unanswered requests belong to the current burst (epoch ms). */
  private unansweredBurstUntil = 0;
  /** Everyone waiting in `whenReachable` for the server to be seen answering. */
  private reachableWaiters: (() => void)[] = [];
  /** Reachability checks queued or running, so a waiter can tell whether one is coming. */
  private reachChecksOutstanding = 0;
  /** When `whenReachable` last asked for a check of its own (epoch ms). */
  private lastWaiterCheckAt = 0;
  private waiterCheckTimer: ReturnType<typeof setTimeout> | undefined;

  /**
   * When the next reachability check will run while offline, as epoch ms, or undefined when none
   * is scheduled. The overlay counts down to it (rule 3), rather than to the transport's own retry.
   */
  nextReachabilityCheckAt: number | undefined;

  constructor(private readonly deps: ConnectionHealthDeps) {
    this.transportState = deps.transport.state;
  }

  get state(): ConnectionHealthState {
    return this._state;
  }

  /** The live-update connection's state as last reported, for the overlay to show beside the server's. */
  get liveUpdates(): LiveUpdateState {
    return this.transportState;
  }

  /** Whether the server answered the last reachability check; undefined before the first. */
  get serverReachable(): boolean | undefined {
    return this.reachable;
  }

  start(): void {
    if (this.started) return;
    this.started = true;

    this.transportState = this.deps.transport.state;
    if (this.transportState !== 'connected') {
      this.transportDownSince = Date.now();
      this.scheduleNoticeCheck();
      // Live updates are not connected yet, so ask now. This runs on nearly every page load: the
      // adapter starts `disconnected` and the stream has not opened when the monitor starts, so it
      // costs one /healthz and one sign-in check per boot, and the open that usually follows
      // clears the minute timer. Without it, a page opened against an unreachable server would
      // not show offline for a minute, because no drop from connected ever comes to set the
      // checks off (reviewer findings F2 and R2, U8b).
      this.request({ reach: true, signIn: true });
    }

    this.disposers.push(
      this.deps.transport.onState((state) => this.onTransport(state))
    );
    const onOffline = () => this.evaluate();
    const onOnline = () => this.request({ reach: true, signIn: true });
    const onVisibility = () => this.onVisibility();
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    this.disposers.push(() => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
    });

    if (document.visibilityState === 'visible') this.startSignInInterval();
    this.evaluate();
  }

  stop(): void {
    this.disposers.forEach((dispose) => dispose());
    this.disposers = [];
    this.clearOfflineTimer();
    this.clearNoticeTimer();
    this.stopSignInInterval();
    if (this.waiterCheckTimer) clearTimeout(this.waiterCheckTimer);
    this.waiterCheckTimer = undefined;
    this.started = false;
  }

  /**
   * Resolves the next time a reachability check finds the server answering (rule 8's first-load
   * clause, D-144): a first load that got no answer waits on this, presenting nothing, and loads
   * again when it resolves. One signal, owned here, so no page polls the server itself.
   *
   * Only a check that finishes after the call counts, never the answer before it: the request that
   * got no answer came after that answer, so it is already out of date. If no check is coming (the
   * page is not offline, and the check the failure set off was skipped as part of a burst), one is
   * asked for, at most once per burst window: a load that keeps getting no answer while the server
   * answers its health check (one endpoint down, say) then retries every ten seconds, not in a
   * tight loop. While offline the backoff loop's own checks answer it.
   */
  whenReachable(): Promise<void> {
    return new Promise((resolve) => {
      this.reachableWaiters.push(resolve);
      this.ensureCheckForWaiters();
    });
  }

  /** Makes sure a reachability check is coming for the waiters, as `whenReachable` describes. */
  private ensureCheckForWaiters(): void {
    if (this.reachableWaiters.length === 0) return;
    if (this.reachChecksOutstanding > 0 || this.waiterCheckTimer) return;
    if (this._state === 'offline') return;
    const wait = Math.max(
      0,
      this.lastWaiterCheckAt + UNANSWERED_BURST_WINDOW_MS - Date.now()
    );
    this.waiterCheckTimer = setTimeout(() => {
      this.waiterCheckTimer = undefined;
      this.lastWaiterCheckAt = Date.now();
      this.request({ reach: true, signIn: false });
    }, wait);
  }

  /** Hands a finished reachability check's answer to the waiters: answering releases them all. */
  private settleWaiters(): void {
    if (this.reachable !== true || this._state === 'offline') {
      this.ensureCheckForWaiters();
      return;
    }
    const waiters = this.reachableWaiters;
    this.reachableWaiters = [];
    waiters.forEach((resolve) => resolve());
  }

  /**
   * A request got no answer (rule 8, D-144): find out at once whether the server is there, through
   * the same queue as every other check, and let the answer decide. Reachable with the sign-in
   * holding is a single failure, told once through `onRequestUnanswered`; unreachable is an outage,
   * and the table raises the offline overlay with no notice beside it.
   *
   * Ignored while offline (the overlay already says it, and the backoff loop is already asking),
   * while signed out, before start, and within the burst window of the last one.
   */
  checkAfterUnansweredRequest(): void {
    if (!this.started) return;
    if (this._state === 'offline' || this._state === 'signed-out') return;
    const now = Date.now();
    if (now < this.unansweredBurstUntil) return;
    this.unansweredBurstUntil = now + UNANSWERED_BURST_WINDOW_MS;
    this.reachChecksOutstanding++;
    this.queue = this.queue.then(async () => {
      await this.probe({ reach: true, signIn: true });
      if (this._state !== 'offline' && this._state !== 'signed-out') {
        this.deps.onRequestUnanswered();
      }
    });
  }

  private onTransport(next: LiveUpdateState): void {
    const previous = this.transportState;
    this.transportState = next;
    if (next === 'connected') {
      this.transportDownSince = undefined;
      this.clearNoticeTimer();
      this.evaluate();
      this.request({ reach: false, signIn: true });
      return;
    }
    if (previous === 'connected') {
      this.transportDownSince = Date.now();
      this.scheduleNoticeCheck();
      this.evaluate();
      this.request({ reach: true, signIn: true });
      return;
    }
    // Down to down (reconnecting <-> disconnected): the page's state does not change with it.
    this.evaluate();
  }

  private onVisibility(): void {
    if (document.visibilityState === 'visible') {
      this.startSignInInterval();
      this.request({ reach: false, signIn: true });
    } else {
      this.stopSignInInterval();
    }
  }

  /**
   * Every minute while live updates are down (and the page is not already offline), check both
   * answers afresh and decide again. The first of these is when the developer notice may apply,
   * and the notice is only true while the server is STILL reachable and the sign-in STILL holds.
   * Repeating matters as much as the first: a down stream raises no further events the monitor
   * sees, so without it a server lost after the first check would never be noticed and the notice
   * would go on saying the server is reachable (reviewer finding F1, U8b).
   */
  private scheduleNoticeCheck(): void {
    this.clearNoticeTimer();
    this.noticeTimer = setTimeout(() => {
      this.noticeTimer = undefined;
      this.request({ reach: true, signIn: true });
      if (this.transportState !== 'connected') this.scheduleNoticeCheck();
    }, LIVE_UPDATES_FAILING_AFTER_MS);
  }

  private clearNoticeTimer(): void {
    if (this.noticeTimer) clearTimeout(this.noticeTimer);
    this.noticeTimer = undefined;
  }

  private startSignInInterval(): void {
    this.stopSignInInterval();
    this.signInInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.request({ reach: false, signIn: true });
      }
    }, SIGN_IN_CHECK_INTERVAL_MS);
  }

  private stopSignInInterval(): void {
    if (this.signInInterval) clearInterval(this.signInInterval);
    this.signInInterval = undefined;
  }

  /** Queue a check; checks run one after another so their answers never interleave. */
  private request(what: { reach: boolean; signIn: boolean }): void {
    if (what.reach) this.reachChecksOutstanding++;
    this.queue = this.queue.then(() => this.probe(what));
  }

  /**
   * One check. A caller that asks for reachability has already counted it in
   * `reachChecksOutstanding`, and it is uncounted here once answered, so `whenReachable` always
   * knows whether a check is still coming.
   */
  private async probe(what: { reach: boolean; signIn: boolean }) {
    try {
      await this.runChecks(what);
    } finally {
      if (what.reach) this.reachChecksOutstanding--;
    }
    if (what.reach) this.settleWaiters();
  }

  private async runChecks(what: { reach: boolean; signIn: boolean }) {
    if (what.reach) this.reachable = await this.deps.checkReachable();
    // The sign-in cannot be asked of a server that did not answer, and a refusal from it would be
    // meaningless; the offline loop asks again once it answers.
    if (what.signIn && this.reachable !== false && navigator.onLine) {
      this.signIn = await this.deps.checkSignIn();
      // A sign-in check with no usable answer may be the server gone: find out, rather than
      // carrying on as though it still answers (the 5-minute check asks the sign-in alone).
      if (this.signIn === 'unknown' && !what.reach) {
        this.reachable = await this.deps.checkReachable();
      }
    }
    this.evaluate();
  }

  /** Apply the table, act on a change, and keep the offline loop running only while offline. */
  private evaluate(): void {
    const previous = this._state;
    let next = this.decide();

    if (next === 'offline') {
      // Nothing about live updates is worth a notice while the server itself is unreachable.
      this.clearNoticeTimer();
    } else if (previous === 'offline' && this.transportState !== 'connected') {
      // The outage does not count towards the minute of rule 6: live updates could not have
      // worked while the server was down. The minute starts now, as the transport is asked to
      // reconnect below, so a recovery never opens straight onto the developer notice.
      this.transportDownSince = Date.now();
      this.scheduleNoticeCheck();
      next = this.decide();
    }

    this._state = next;

    // A newly scheduled check is announced even when the state is unchanged, so the overlay's
    // countdown restarts from it rather than sitting at zero after the last one.
    let checkScheduled = false;
    if (next === 'offline') {
      if (!this.offlineTimer) {
        this.scheduleOfflineCheck();
        checkScheduled = true;
      }
    } else {
      this.clearOfflineTimer();
      this.offlineAttempt = 0;
      // The outage is over: a request that goes unanswered from here is a new event, not part
      // of the burst that found the outage.
      if (previous === 'offline') this.unansweredBurstUntil = 0;
    }

    if (next === previous) {
      if (checkScheduled) this.deps.onState(next);
      return;
    }
    this.deps.onState(next);

    // The table only reaches signed-out from a refused or mismatched sign-in, so the answer that
    // got it there is one of the two; the guard keeps that claim checked rather than cast.
    if (
      next === 'signed-out' &&
      (this.signIn === 'refused' || this.signIn === 'mismatch')
    ) {
      this.deps.onSignedOut(this.signIn);
    }
    // Leaving offline: the server answers again, so bring live updates back with it now rather
    // than whenever the transport's own backoff next fires.
    if (previous === 'offline' && this.transportState !== 'connected') {
      this.deps.transport.reconnectNow();
    }
  }

  /** The table applied to what the monitor currently knows. */
  private decide(): ConnectionHealthState {
    return decideConnectionHealth({
      transport: this.transportState,
      reachable: this.reachable,
      signIn: this.signIn,
      transportDownSince: this.transportDownSince,
      now: Date.now(),
      navigatorOnline: navigator.onLine,
    });
  }

  /** The next reachability check of an outage, on the backoff; then the sign-in; then the table. */
  private scheduleOfflineCheck(): void {
    const wait = offlineBackoffMs(this.offlineAttempt);
    this.nextReachabilityCheckAt = Date.now() + wait;
    const handle = setTimeout(() => {
      this.nextReachabilityCheckAt = undefined;
      this.offlineAttempt++;
      this.reachChecksOutstanding++;
      // The handle is released only when the queued check actually starts, so an evaluation that
      // happens in between (a transport event, say) does not schedule a second check alongside it.
      // Released only if it is still THIS check's: a recovery and a new outage in between have
      // scheduled a newer one, whose handle must not be dropped (reviewer finding F8, U8b).
      this.queue = this.queue.then(async () => {
        if (this.offlineTimer === handle) this.offlineTimer = undefined;
        await this.probe({ reach: true, signIn: true });
      });
    }, wait);
    this.offlineTimer = handle;
  }

  private clearOfflineTimer(): void {
    if (this.offlineTimer) clearTimeout(this.offlineTimer);
    this.offlineTimer = undefined;
    this.nextReachabilityCheckAt = undefined;
  }
}
